/**
 * Client for user-moderation endpoints (blocks + reports).
 *
 * Mirrors backend/api/partner.py and backend/api/admin/moderation.py.
 */

import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/apiUtils';

export type ReportCategory =
  | 'harassment'
  | 'spam'
  | 'sexual_content'
  | 'hate_speech'
  | 'minor_safety'
  | 'scam_or_fraud'
  | 'impersonation'
  | 'self_harm'
  | 'other';

export type ReportStatus = 'pending' | 'reviewed' | 'actioned' | 'dismissed';

export interface BlockedUserItem {
  readonly blockedUserId: string;
  readonly blockedUserName: string;
  readonly reason: string | null;
  readonly createdAt: string;
}

export interface BlockStatus {
  readonly iBlockedThem: boolean;
  readonly theyBlockedMe: boolean;
}

export interface CreateReportPayload {
  readonly reportedUserId: string;
  readonly category: ReportCategory;
  readonly description: string;
  readonly messageId?: string;
  readonly requestId?: string;
}

export interface ReportListItem {
  readonly id: string;
  readonly reporterUserId: string | null;
  readonly reporterUserName: string | null;
  readonly reportedUserId: string | null;
  readonly reportedUserName: string | null;
  readonly category: ReportCategory;
  readonly status: ReportStatus;
  readonly createdAt: string;
}

export interface ReportDetail extends ReportListItem {
  readonly description: string;
  readonly adminNotes: string | null;
  readonly actionTaken: string | null;
  readonly reviewedByUserName: string | null;
  readonly reviewedAt: string | null;
  readonly messageId: string | null;
  readonly messageIsEncrypted: boolean | null;
  readonly requestId: string | null;
  readonly updatedAt: string | null;
}

export interface ReportSummary {
  readonly totalCount: number;
  readonly pendingCount: number;
  readonly reviewedCount: number;
  readonly actionedCount: number;
  readonly dismissedCount: number;
  readonly last7DaysCount: number;
}

export interface ReportListResponse {
  readonly items: readonly ReportListItem[];
  readonly total: number;
  readonly page: number;
  readonly totalPages: number;
  readonly limit: number;
}

// ============================================
// User-facing
// ============================================

export async function listMyBlocks(): Promise<readonly BlockedUserItem[]> {
  const data = await apiGet<{ blocks: BlockedUserItem[] }>('/partner/blocks', {
    errorMessage: 'Failed to load blocked users',
    context: 'listMyBlocks',
  });
  return data?.blocks ?? [];
}

export async function getBlockStatus(otherUserId: string): Promise<BlockStatus | null> {
  return apiGet<BlockStatus>(`/partner/blocks/status/${otherUserId}`, {
    errorMessage: 'Failed to check block status',
    context: 'getBlockStatus',
  });
}

export async function blockUser(
  blockedUserId: string,
  reason?: string,
): Promise<boolean> {
  const res = await apiPost<{ message: string }>(
    '/partner/blocks',
    { blockedUserId, reason },
    { errorMessage: 'Failed to block user', context: 'blockUser' },
  );
  return res !== null;
}

export async function unblockUser(blockedUserId: string): Promise<boolean> {
  const res = await apiDelete<{ message: string }>(
    `/partner/blocks/${blockedUserId}`,
    { errorMessage: 'Failed to unblock user', context: 'unblockUser' },
  );
  return res !== null;
}

export async function submitReport(payload: CreateReportPayload): Promise<string | null> {
  const res = await apiPost<{ reportId: string }>('/partner/reports', payload, {
    errorMessage: 'Failed to submit report',
    context: 'submitReport',
  });
  return res?.reportId ?? null;
}

// ============================================
// Admin
// ============================================

export async function fetchModerationSummary(): Promise<ReportSummary | null> {
  return apiGet<ReportSummary>('/admin/moderation/reports/summary', {
    errorMessage: 'Failed to load moderation summary',
    context: 'fetchModerationSummary',
  });
}

export async function fetchModerationReports(
  page: number,
  limit: number,
  status?: ReportStatus,
): Promise<ReportListResponse | null> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (status) params.set('status', status);
  return apiGet<ReportListResponse>(`/admin/moderation/reports?${params.toString()}`, {
    errorMessage: 'Failed to load reports',
    context: 'fetchModerationReports',
  });
}

export async function fetchModerationReportDetail(
  reportId: string,
): Promise<ReportDetail | null> {
  return apiGet<ReportDetail>(`/admin/moderation/reports/${reportId}`, {
    errorMessage: 'Failed to load report',
    context: 'fetchModerationReportDetail',
  });
}

export async function updateModerationReport(
  reportId: string,
  update: { status?: ReportStatus; adminNotes?: string; actionTaken?: string },
): Promise<ReportDetail | null> {
  return apiPatch<ReportDetail>(`/admin/moderation/reports/${reportId}`, update, {
    errorMessage: 'Failed to update report',
    context: 'updateModerationReport',
  });
}
